import { useCallback, useEffect, useState } from "react";
import { execService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Radar, Loader2 } from "lucide-react";
import { useTenantScope, TenantPicker, SelectTenantNotice, PageHeader, SEV_STYLE } from "./shared";

const RISK_STYLE = { critical: SEV_STYLE.critical, high: SEV_STYLE.high, medium: SEV_STYLE.medium };

export default function CustomerRiskRadar() {
  const scope = useTenantScope();
  const { isSuper, tenantId, params } = scope;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const blocked = isSuper && !tenantId;

  const load = useCallback(() => {
    if (blocked) { setData(null); return; }
    setLoading(true);
    execService.riskRadar(params).then(setData).catch((e) => toast.error(apiErrorMessage(e.response?.data?.detail))).finally(() => setLoading(false));
  }, [blocked, params]);
  useEffect(() => { load(); }, [load]);

  const items = data?.items || [];

  return (
    <div data-testid="exec-risk-radar-page">
      <PageHeader icon={Radar} title="Customer Risk Radar" subtitle="Customers showing churn signals from health, tickets & escalations.">
        <TenantPicker {...scope} />
      </PageHeader>

      {blocked ? <SelectTenantNotice /> : loading ? (
        <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center" data-testid="risk-empty">
          <p className="text-sm text-muted-foreground">No at-risk customers detected from current canonical signals. 🎉</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="risk-list">
          {items.map((c) => (
            <div key={c.id} className="bg-card border border-border rounded-xl p-4 flex items-start gap-3" data-testid={`risk-row-${c.id}`}>
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${RISK_STYLE[c.risk] || RISK_STYLE.medium} shrink-0`}>{c.risk}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-foreground">{c.customerName} <span className="text-[11px] text-muted-foreground font-normal">{c.email}</span></p>
                <p className="text-[11px] text-muted-foreground">Health: {c.band}{c.score != null ? ` (${c.score})` : ""} · {c.lifecycleStage}</p>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {c.drivers.map((d, i) => <span key={i} className="text-[10px] text-muted-foreground bg-muted/40 border border-border rounded px-1.5 py-0.5">{d}</span>)}
                </div>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-bold text-foreground">{c.openTickets}</p>
                <p className="text-[10px] text-muted-foreground">open tickets</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
